import { Color as PersonaColor, Spacing } from '@persona-id/design-tokens';
import { MapPinIcon, MaximizeIcon, ZoomInIcon, ZoomOutIcon } from '@persona-id/design-tokens/icons';
import { keyBy } from 'lodash/fp';
import { MutableRefObject, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { ACTIONS_NODE_SIZE } from 'dashboard/apps/InquiryTemplateEditor/ActionsNode/ActionsNode';
import { CONDITIONAL_NODE_HEIGHT } from 'dashboard/apps/InquiryTemplateEditor/ConditionalNode/ConditionalNode';
import { CONDITIONAL_ROOT_SIZE } from 'dashboard/apps/InquiryTemplateEditor/ConditionalRootNode/ConditionalRootNode';
import { CONNECTOR_NODE_SIZE } from 'dashboard/apps/InquiryTemplateEditor/ConnectorNode/ConnectorNode';
import { buildInspectTree } from 'dashboard/apps/InquiryTemplateEditor/InquiryInspect/utils';
import { generatePages } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/pages';
import {
  CombinedRunStepForTree,
  CombinedStepForTree,
  LinkedWorkflowRunStep,
  LinkedWorkflowStep,
  PreviewPageMap,
  type StepPreviewMap,
  TemplateRunStepPreview,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  buildGraph,
  extractActionsAndConditionalNodes,
  mapStepsPreviewAndToken,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import { getConditionalStepForToken } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import { mapStepsConfigToStepsPreview } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import {
  InquiryTemplateTreeProvider,
  useInquiryTemplateTreeStore,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/InquiryTemplateTreeProvider';
import InquiryTemplateVersionTreeBase from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionTreeBase/InquiryTemplateVersionTreeBase';
import { firstInitialStep } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import WorkflowConditionalPreview from 'dashboard/apps/InquiryTemplateEditor/WorkflowConditionalPreview/WorkflowConditionalPreview';
import {
  FlowEditorTransitionNodeHighlighter,
  TransitionElement,
} from 'dashboard/components/FlowEditorNodeHighlighter';
import PanZoomBox, { usePanZoomBoxControls } from 'dashboard/components-lib/PanZoomBox';
import { createMockDirectTrigger } from 'dashboard/lib/stubs/trigger';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionSchema,
} from 'dashboard/models/inquiry-template-version';
import { WorkflowSchema } from 'dashboard/models/workflow-schema';
import { WorkflowStepConditional, WorkflowStepType } from 'dashboard/models/workflow-version';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { Elevation } from 'design-system/variables/effect';
import { StepPreview } from 'lib/interfaces/StepsConfig';
import { FRAME_HEIGHT } from 'verify/components/Application/Frame';

export enum ActiveNodeElement {
  CurrentStep = 'currentStep',
  PreviousStep = 'previousStep',
  Connector = 'connector',
}

const ContentContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
`;

const ZoomControlsContainer = styled.div`
  position: absolute;
  top: ${Spacing[4]};
  right: ${Spacing[4]};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: ${Spacing[9]};
  padding: ${Spacing[2]};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  box-shadow: ${Elevation.High};
  z-index: ${Z_INDICES.modalElevated};
`;

const ZoomButton = styled(Button).attrs(() => ({
  width: 'icon',
  variant: 'basic',
}))`
  svg {
    width: 18px;
    height: 18px;
  }
`;

const ZoomControls = ({
  onFitActiveContent,
  onMaximizeContent,
}: {
  onFitActiveContent: () => void;
  onMaximizeContent: () => void;
}) => {
  const {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    zoomControls: { zoomIn, zoomOut, percentage, minScale, maxScale },
  } = usePanZoomBoxControls();
  const atMaxZoomIn = percentage / 100 >= maxScale;
  const atMaxZoomOut = percentage / 100 <= minScale;
  return (
    <ZoomControlsContainer>
      <HStack spacing={Spacing[2]}>
        <ZoomButton
          id='inquiry-run__maximimize-content'
          aria-label='Maximize Content'
          onClick={onMaximizeContent}
        >
          <MaximizeIcon />
        </ZoomButton>
        <ZoomButton
          id='inquiry-run__target-active-nodes-button'
          aria-label='Target active nodes'
          onClick={onFitActiveContent}
        >
          <MapPinIcon />
        </ZoomButton>
        <ZoomButton
          id='inquiry-run__zoom-in-button'
          aria-label='Zoom in'
          aria-disabled={atMaxZoomIn}
          disabled={atMaxZoomIn}
          onClick={zoomIn}
        >
          <ZoomInIcon />
        </ZoomButton>
        <ZoomButton
          id='inquiry-run__zoom-out-button'
          aria-label='Zoom out'
          aria-disabled={atMaxZoomOut}
          disabled={atMaxZoomOut}
          onClick={zoomOut}
        >
          <ZoomOutIcon />
        </ZoomButton>
      </HStack>
    </ZoomControlsContainer>
  );
};

interface InquiryInspectRunTreeInnerProps {
  inquiryTemplateVersion: InquiryTemplateVersion;
  workflowVersionMap: WorkflowVersionMap;
  stepsTree: CombinedStepForTree[] | CombinedRunStepForTree[];
  transitionStateRef: MutableRefObject<{
    currentStepName?: string;
    previousStepName?: string;
  }>;
  activeNodeElementsRef: MutableRefObject<Record<string, { id: string; element: HTMLDivElement }>>;
  numTransitions: number;
  workflowSchema: WorkflowSchema;
}

const InquiryInspectRunTreeInner = ({
  transitionStateRef,
  activeNodeElementsRef,
  numTransitions,
  inquiryTemplateVersion,
  workflowVersionMap,
  workflowSchema,
  stepsTree,
}: InquiryInspectRunTreeInnerProps) => {
  numTransitions;

  const { centerArrayTargets, centerTarget, zoomableRef } = usePanZoomBoxControls();

  useEffect(() => {
    const elements = Object.values(activeNodeElementsRef.current).map((element) => element.element);
    if (elements.length > 0) {
      centerArrayTargets({ doms: elements });
    }
    // Automatically center target only if the current step has changed
  }, [transitionStateRef.current.currentStepName]);

  const renderScreenPreviewNodeHighlighter = (step: TemplateRunStepPreview) => {
    if (step.name === transitionStateRef.current.currentStepName) {
      return (
        <FlowEditorTransitionNodeHighlighter
          height={FRAME_HEIGHT}
          transitionElement={TransitionElement.CurrentStep}
          ref={(element) => {
            if (element == null) return;
            activeNodeElementsRef.current[ActiveNodeElement.CurrentStep] = {
              id: step.token,
              element: element,
            };
          }}
        />
      );
    } else if (step.name === transitionStateRef.current.previousStepName) {
      return (
        <FlowEditorTransitionNodeHighlighter
          height={FRAME_HEIGHT}
          transitionElement={TransitionElement.PreviousStep}
          ref={(element) => {
            if (element == null) return;
            activeNodeElementsRef.current[ActiveNodeElement.PreviousStep] = {
              id: step.token,
              element: element,
            };
          }}
        />
      );
    }
  };

  const getScreenFlowConnectorStyles = (
    prevStep: CombinedRunStepForTree,
    nextStep: CombinedRunStepForTree,
  ) => {
    let color;
    let strokeWidth;

    if (
      // If the previous step is visual, it is not marked as being in the run path, so we need
      // to check against the previous step in the transitionStateRef
      ((transitionStateRef.current.previousStepName === prevStep.token || prevStep.isInRunPath) &&
        transitionStateRef.current.currentStepName === nextStep.token) ||
      nextStep.isInRunPath ||
      // edge case when the prevStep and nextStep are reversed in the case of back transition,
      // but ensure we don't falsely highlight if next step is a connector
      (transitionStateRef.current.previousStepName === nextStep.token &&
        transitionStateRef.current.currentStepName === prevStep.token &&
        !nextStep.isConnector)
    ) {
      color = PersonaColor.CoreBlue700;
      strokeWidth = 8;
    }

    return {
      color,
      strokeWidth,
    };
  };

  const renderActionsNodeHighlighter = (step: LinkedWorkflowRunStep) => {
    if (step.isInRunPath) {
      return (
        <FlowEditorTransitionNodeHighlighter
          height={ACTIONS_NODE_SIZE}
          transitionElement={TransitionElement.CurrentStep}
        />
      );
    }
  };

  const renderWrappedConnectorNodeHighlighter = (step: CombinedRunStepForTree) => {
    if (step.isInRunPath) {
      return (
        <FlowEditorTransitionNodeHighlighter
          height={CONNECTOR_NODE_SIZE}
          transitionElement={TransitionElement.CurrentStep}
          ref={(element) => {
            if (element == null) return;
            activeNodeElementsRef.current[ActiveNodeElement.Connector] = {
              id: step.token,
              element: element,
            };
          }}
        />
      );
    } else if (activeNodeElementsRef.current[ActiveNodeElement.Connector]?.id === step.token) {
      // The step is not in the run path and it was previously an "active node",
      // ensure that we remove it from that hash
      delete activeNodeElementsRef.current[ActiveNodeElement.Connector];
    }
  };

  const getBranchRootScreenFlowConnectorStyles = (
    rootStep: CombinedRunStepForTree,
    branchIndex: number,
  ) => {
    let color;
    let strokeWidth;
    if (rootStep.isInRunPath && rootStep.chosenIndexes?.includes(branchIndex)) {
      color = PersonaColor.CoreBlue700;
      strokeWidth = 8;
    }

    return {
      color,
      strokeWidth,
    };
  };

  const getPathHighlightInformationForStep = (step: CombinedRunStepForTree) => {
    if (Array.isArray(step.branches) && step.isInRunPath && (step.chosenIndexes?.length ?? 0) > 0) {
      const runPathIndexes = step.chosenIndexes;
      const childTokens = step.branches.map((branchSteps) =>
        branchSteps.length > 0 ? branchSteps[0].token : '',
      );
      const highlightPercentage =
        runPathIndexes != null && Math.max(...runPathIndexes) > 0
          ? (100 / (childTokens.length - 1)) * Math.max(...runPathIndexes)
          : undefined;

      return {
        highlightColor: PersonaColor.CoreBlue700,
        branchStrokeWidth: 8,
        runPathIndexes: runPathIndexes,
        highlightPercentage: highlightPercentage,
      };
    }
    return {};
  };

  const renderConditionalRootNodeHighlighter = (step: LinkedWorkflowRunStep) => {
    if (step.isInRunPath) {
      return (
        <FlowEditorTransitionNodeHighlighter
          height={CONDITIONAL_ROOT_SIZE}
          transitionElement={TransitionElement.CurrentStep}
        />
      );
    }
  };

  const renderConditionalNodeHighlighter = (
    step: LinkedWorkflowRunStep,
    _workflowStep: WorkflowStepConditional,
    branchIndex: number,
  ) => {
    if (step.isInRunPath && step.chosenIndexes?.includes(branchIndex)) {
      return (
        <FlowEditorTransitionNodeHighlighter
          height={CONDITIONAL_NODE_HEIGHT}
          transitionElement={TransitionElement.CurrentStep}
        />
      );
    }
  };

  const getActionNodeWorkflowSteps = (step: LinkedWorkflowRunStep) => {
    return workflowVersionMap[step.workflowVersionId]?.attributes?.['steps-config'];
  };

  const getConditionalNodeTooltipContents = (step: LinkedWorkflowRunStep) => {
    const workflowVersion = workflowVersionMap[step.workflowVersionId];
    const conditionalStep = getConditionalStepForToken(step.workflowStep.token, workflowVersion);

    if (conditionalStep?.type === WorkflowStepType.Conditional) {
      return (
        <WorkflowConditionalPreview
          trigger={createMockDirectTrigger(workflowVersion?.relationships?.trigger?.data?.id)}
          step={conditionalStep}
          stepsConfig={workflowVersion.attributes['steps-config']}
          workflowSchema={workflowSchema}
          getInInquiryInspectRunPath={(routeNumber) => {
            return step.chosenIndexes?.includes(routeNumber) ?? false;
          }}
        />
      );
    }
  };

  return (
    <>
      <ZoomControls
        onFitActiveContent={() => {
          const elements = Object.values(activeNodeElementsRef.current).map(
            (element) => element.element,
          );
          if (elements.length > 0) {
            centerArrayTargets({ doms: elements });
          }
        }}
        onMaximizeContent={() => {
          centerTarget({ dom: zoomableRef.current as HTMLElement });
        }}
      />
      <InquiryTemplateVersionTreeBase
        inquiryTemplateVersion={inquiryTemplateVersion}
        workflowVersionMap={workflowVersionMap}
        tree={stepsTree}
        renderScreenPreviewNodeHighlighter={renderScreenPreviewNodeHighlighter}
        renderActionsNodeHighlighter={renderActionsNodeHighlighter}
        renderConditionalRootNodeHighlighter={renderConditionalRootNodeHighlighter}
        renderConditionalNodeHighlighter={renderConditionalNodeHighlighter}
        renderWrappedConnectorNodeHighlighter={renderWrappedConnectorNodeHighlighter}
        getScreenFlowConnectorStyles={getScreenFlowConnectorStyles}
        getBranchRootScreenFlowConnectorStyles={getBranchRootScreenFlowConnectorStyles}
        getPathHighlightInformationForStep={getPathHighlightInformationForStep}
        getActionNodeWorkflowSteps={getActionNodeWorkflowSteps}
        getConditionalNodeTooltipContents={getConditionalNodeTooltipContents}
      />
    </>
  );
};

interface InquiryInspectRunTreeProps {
  workflowSchema: WorkflowSchema;
  inquiryTemplateVersion: InquiryTemplateVersion;
  schema: InquiryTemplateVersionSchema;
  stepsPreview: StepPreview[];
  transitionStateRef: MutableRefObject<{
    currentStepName?: string;
    previousStepName?: string;
  }>;
  activeNodeElementsRef: MutableRefObject<Record<string, { id: string; element: HTMLDivElement }>>;
  numTransitions: number;
}

const InquiryInspectRunTree = ({
  workflowSchema,
  inquiryTemplateVersion,
  transitionStateRef,
  activeNodeElementsRef,
  numTransitions,
  stepsPreview,
  schema,
}: InquiryInspectRunTreeProps) => {
  // For linter. We use changes in `numTransitions` to trigger rerender since
  // transitionStateRef doesn't
  numTransitions;

  const stepsPreviewMap = useMemo<StepPreviewMap>(
    () =>
      keyBy(
        'name',
        mapStepsConfigToStepsPreview({
          steps: inquiryTemplateVersion.attributes.steps,
          stepsPreview: stepsPreview,
          currentLocale: 'en-US',
        }),
      ),
    [inquiryTemplateVersion.attributes.steps, stepsPreview],
  );

  const previewPageMap = useMemo<PreviewPageMap>(
    () => generatePages(Object.values(stepsPreviewMap), false),
    [stepsPreviewMap],
  );

  const workflowVersionMap = useMemo<WorkflowVersionMap>(
    () => keyBy('id', schema['workflow-versions'].data),
    [schema],
  );

  const extractedWorkflowSteps = useMemo<LinkedWorkflowStep[]>(
    () => extractActionsAndConditionalNodes(workflowVersionMap),
    [workflowVersionMap],
  );

  const [graph, combinedSteps, initialStep] = useMemo(() => {
    const steps = inquiryTemplateVersion.attributes.steps;
    const initialStep = firstInitialStep(steps);
    if (initialStep == null) {
      return [{}, [], null];
    }
    const combinedSteps = [
      ...mapStepsPreviewAndToken({ steps, stepsPreviewMap, previewPageMap }),
      ...extractedWorkflowSteps,
    ];

    return [buildGraph(combinedSteps, initialStep.name, {}), combinedSteps, initialStep];
  }, [
    extractedWorkflowSteps,
    inquiryTemplateVersion.attributes.steps,
    previewPageMap,
    stepsPreviewMap,
  ]);

  const stepsTree = useMemo(() => {
    return buildInspectTree({
      initialStep,
      currentStepName: transitionStateRef.current?.currentStepName ?? null,
      previousStepName: transitionStateRef.current?.previousStepName ?? null,
      combinedSteps,
      graph,
    });
  }, [
    initialStep,
    numTransitions,
    transitionStateRef.current?.currentStepName,
    transitionStateRef.current?.previousStepName,
    combinedSteps,
    graph,
  ]);

  const InquiryFlowEditorStore = useInquiryTemplateTreeStore({
    tree: stepsTree,
  });

  return (
    <InquiryTemplateTreeProvider value={InquiryFlowEditorStore}>
      <ContentContainer>
        <PanZoomBox
          showZoomControls={false}
          zoomToFitPadding={{ top: 100, bottom: 100, left: 100, right: 100 }}
          withExternalZoomControls
          withDynamicUpdates
          zoomToFit
        >
          <InquiryInspectRunTreeInner
            transitionStateRef={transitionStateRef}
            activeNodeElementsRef={activeNodeElementsRef}
            numTransitions={numTransitions}
            inquiryTemplateVersion={inquiryTemplateVersion}
            workflowVersionMap={workflowVersionMap}
            workflowSchema={workflowSchema}
            stepsTree={stepsTree}
          />
        </PanZoomBox>
      </ContentContainer>
    </InquiryTemplateTreeProvider>
  );
};

export default InquiryInspectRunTree;
